import React, { useEffect, useMemo, useState } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";

import { useNotification } from "@hooks/useNotification";
import { useForm, Controller } from "react-hook-form";

import ModalIndex from "@Modals/layout/index";
import DefaultDatePicker from "@components/arhDatePicker/DefaultDatePicker";
import { Switch } from "@components/tailwindComponents";

import { checkErrors } from "@helpers/_functions";
import { SalaryAndAdvantage } from "@services/all.services";

const DATE_FORMAT = "YYYY-MM-DD";

export default function ModalDuplicateSalary(props) {
  const { isOpen, selectedSalary, selectedCareer, onClickClose } = props;
  const { t: translate } = useTranslation([
    "pages.employÃ©s.parcours&Certifications",
    "message"
  ]);
  const { showError, showSuccess } = useNotification();

  const { control, handleSubmit, reset, watch } = useForm();

  const { sourceEndDate, newSalaryStartDate, isInProgress } = watch();

  // -- 🧩 Form initial values 🧩 --
  useEffect(() => {
    if (!isOpen || !selectedSalary?._id) {
      return;
    }

    let sourceEndDate = selectedSalary?.salaryEnd
      ? moment(selectedSalary.salaryEnd, DATE_FORMAT).toDate()
      : new Date();

    const selectedSalaryBeginMoment = moment(
      selectedSalary.salaryBegin,
      DATE_FORMAT
    );

    if (moment(sourceEndDate).isBefore(selectedSalaryBeginMoment)) {
      sourceEndDate = selectedSalaryBeginMoment.toDate();
    }

    const newSalaryStartDate = moment(sourceEndDate).add(1, "day").toDate();

    reset({
      sourceEndDate: sourceEndDate,
      newSalaryStartDate: newSalaryStartDate,
      newSalaryEndDate: null,
      isInProgress: false
    });
  }, [isOpen, selectedSalary?._id]);

  // -- 🧩 Dates Limitations 🧩 --
  const minLimit = useMemo(
    () =>
      selectedSalary?.salaryBegin
        ? moment(selectedSalary.salaryBegin, DATE_FORMAT).toDate()
        : null,
    [selectedSalary]
  );

  const maxLimit = useMemo(
    () =>
      selectedCareer?.endDate
        ? moment(selectedCareer.endDate, DATE_FORMAT).toDate()
        : null,
    [selectedCareer]
  );

  // -- 🧩 Form Validation 🧩 --
  const sourceEndDateValidation = useMemo(() => {
    return {
      required: true
    };
  }, []);

  const newSalaryStartDateValidation = useMemo(() => {
    return {
      required: true,
      validate: {
        startDateAfterSourceEndDate: (value) =>
          moment(value).isAfter(sourceEndDate)
      }
    };
  }, [sourceEndDate]);

  const newSalaryEndDateValidation = useMemo(() => {
    return {
      validate: {
        required: (value) => isInProgress || Boolean(value),
        endDateAfterStartDate: (value) =>
          isInProgress || moment(value).isAfter(newSalaryStartDate)
      }
    };
  }, [newSalaryStartDate, isInProgress]);

  // -- 🧩 Form Action Handling 🧩 --
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmitSuccess = async (formValue) => {
    try {
      setIsLoading(true);

      const salaryBegin = moment(formValue.newSalaryStartDate).format(
        DATE_FORMAT
      );

      let salaryEnd = "";
      if (!formValue.isInProgress) {
        salaryEnd = moment(formValue.newSalaryEndDate).format(DATE_FORMAT);
      }

      const advantageTypes = selectedSalary.AdvantageType.map((adv) => ({
        name: adv.name || "",
        annualValue: adv.annualValue || "",
        annualDevise: adv.annualDevise?._id || "",
        note: adv.note || ""
      }));

      const salaryData = {
        ...selectedSalary,
        _id: undefined,
        salaryBegin,
        salaryEnd,
        isInProgress: formValue.isInProgress,
        AdvantageType: advantageTypes
      };

      if (!selectedSalary.salaryEnd) {
        await SalaryAndAdvantage.updateById({
          ...selectedSalary,
          salaryEnd: moment(formValue.sourceEndDate).format(DATE_FORMAT),
          isInProgress: false
        });
      }

      await SalaryAndAdvantage.insert(salaryData);

      showSuccess("creation_action_success", { object: "salary" });
      onClickClose();
    } catch (error) {
      console.error(error);
      showError("error_default");
    }

    setIsLoading(false);
  };

  function handleSubmitError(formErrors) {
    const errorArray = Object.values(formErrors);

    if (checkErrors(errorArray, "required")) {
      showError("field_obligatory");
      return;
    }

    if (checkErrors(errorArray, "startDateAfterSourceEndDate")) {
      showError("verification_two_object", {
        object1: "startDateUpper",
        operator: "superiour",
        object2: "duplicatedEchelon"
      });
      return;
    }

    if (checkErrors(errorArray, "endDateAfterStartDate")) {
      showError("verification_two_object", {
        object1: "endDate",
        operator: "superiour",
        object2: "startDate"
      });
      return;
    }
  }

  return (
    <ModalIndex
      isOpen={isOpen}
      widthType="small"
      title={translate("Dupliquer le parcours")}
      onClickClose={onClickClose}
      isLoading={isLoading}
      buttonType="double"
      buttonOptions={{
        click: handleSubmit(handleSubmitSuccess, handleSubmitError),
        color: "green",
        title: translate("Enregistrer")
      }}
      buttonCancelOptions={{
        click: onClickClose,
        color: "outlined",
        title: translate("Annuler")
      }}
    >
      <div className="arh-flex arh-flex-col arh-gap-4">
        <div>
          <label className="arh-mb-1 arh-block arh-text-left arh-font-poppins arh-text-[11px] arh-font-medium arh-text-darkColor">
            {translate("Date de fin de l'échelon dupliqué")}
          </label>
          <div className="arhDatePicker employerThemeDatePicker blackSvgColor calendar_with_btn datepicker_vacation arh-relative arh-w-full">
            <Controller
              name="sourceEndDate"
              control={control}
              rules={sourceEndDateValidation}
              render={({
                field: { value, onChange },
                fieldState: { invalid }
              }) => (
                <DefaultDatePicker
                  dateValue={value}
                  setDateValue={onChange}
                  placeholder={translate("Date de fin")}
                  classPosition="calendar_right"
                  readOnly={selectedSalary?.salaryEnd}
                  minDate={minLimit}
                  maxDate={maxLimit}
                  invalid={invalid}
                />
              )}
            />
          </div>
        </div>

        <div>
          <label className="arh-mb-1 arh-block arh-text-left arh-font-poppins arh-text-[11px] arh-font-medium arh-text-darkColor">
            {translate("Date de démarrage du nouvel échelon")}
          </label>
          <div className="arhDatePicker employerThemeDatePicker blackSvgColor calendar_with_btn datepicker_vacation arh-relative arh-w-full">
            <Controller
              name="newSalaryStartDate"
              control={control}
              rules={newSalaryStartDateValidation}
              render={({
                field: { value, onChange },
                fieldState: { invalid }
              }) => (
                <DefaultDatePicker
                  dateValue={value}
                  setDateValue={onChange}
                  placeholder={translate("Date de d'émarrage")}
                  classPosition="calendar_right"
                  minDate={minLimit}
                  maxDate={maxLimit}
                  invalid={invalid}
                />
              )}
            />
          </div>
        </div>

        <div>
          <label className="arh-mb-1 arh-block arh-text-left arh-font-poppins arh-text-[11px] arh-font-medium arh-text-darkColor">
            {translate("Date de fin du nouvel échelon")}
          </label>
          <div className="arhDatePicker employerThemeDatePicker blackSvgColor calendar_with_btn datepicker_vacation arh-relative arh-w-full">
            <Controller
              name="newSalaryEndDate"
              control={control}
              rules={newSalaryEndDateValidation}
              render={({
                field: { value, onChange },
                fieldState: { invalid }
              }) => (
                <DefaultDatePicker
                  dateValue={value}
                  setDateValue={onChange}
                  placeholder={translate("Date de fin")}
                  classPosition="calendar_right"
                  readOnly={isInProgress}
                  minDate={minLimit}
                  maxDate={maxLimit}
                  invalid={invalid}
                />
              )}
            />
          </div>
        </div>

        <div className="arh-flex arh-items-center arh-justify-end">
          <span className="arh-mr-2 arh-font-poppins arh-text-xs arh-font-medium arh-text-darkColor">
            {translate("En cours")}
          </span>
          <Controller
            name="isInProgress"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Switch
                color="arh-bg-greenColor"
                checked={value}
                handleChange={() => onChange(!value)}
                disabled={Boolean(selectedCareer?.endDate)}
              />
            )}
          />
        </div>
      </div>
    </ModalIndex>
  );
}
